import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { STORAGE_API_BASE, STORAGE_PREVIEW_BASE } from "@/utils/constant";
import { getMediaAuthStatus, subscribeMediaAuth } from "@/utils/media-auth";
import { getAccessToken } from "@/utils/tokenManager";

const stripTrailingSlash = (value: string) => value.replace(/\/$/, "");

// Media served by our own storage must carry the Authorization header, the same
// way the video player authorizes its playlist requests. Anything hosted
// elsewhere has to be requested untouched — the header would break CORS.
export const isAuthorizedMediaUrl = (url?: string): boolean => {
  if (!url) return false;
  if (url.startsWith("blob:") || url.startsWith("data:")) return false;
  if (url.startsWith("/")) return true;
  return [STORAGE_PREVIEW_BASE, STORAGE_API_BASE]
    .filter(Boolean)
    .some((base) => url.startsWith(stripTrailingSlash(base)));
};

// <img> and <audio> cannot send headers, so the bytes are fetched by hand and
// handed to the element as an object URL.
export const fetchAuthorizedObjectUrl = async (url: string, signal?: AbortSignal): Promise<string> => {
  const token = getAccessToken();
  const response = await fetch(url, {
    signal,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) throw new Error(`fetch media failed (${response.status})`);
  return URL.createObjectURL(await response.blob());
};

interface AuthorizedMediaUrlResult {
  /** Object URL for authorized media, or the original url when no auth is needed. */
  src: string;
  isLoading: boolean;
  error: string | null;
}

interface AuthorizedMediaUrlOptions {
  /**
   * Play/render straight from the url when the media service worker can sign
   * the request, so the browser streams byte ranges instead of waiting for the
   * whole file. Falls back to the object url when the worker is unavailable.
   */
  stream?: boolean;
}

/** Whether the service worker is signing media requests for us right now. */
export function useMediaAuthStatus() {
  return useSyncExternalStore(subscribeMediaAuth, getMediaAuthStatus, () => "unavailable" as const);
}

export function useAuthorizedMediaUrl(
  url?: string,
  { stream = false }: AuthorizedMediaUrlOptions = {},
): AuthorizedMediaUrlResult {
  const needsAuth = useMemo(() => isAuthorizedMediaUrl(url), [url]);
  const mediaAuthStatus = useMediaAuthStatus();
  const canStream = stream && needsAuth && mediaAuthStatus === "ready";
  // Deciding between the two paths has to wait for the worker handshake,
  // otherwise a full download starts and is thrown away a moment later.
  const isDecidingTransport = stream && needsAuth && mediaAuthStatus === "pending";

  const [objectUrl, setObjectUrl] = useState("");
  const [isLoading, setIsLoading] = useState(needsAuth);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (canStream || isDecidingTransport) {
      setObjectUrl("");
      setIsLoading(isDecidingTransport);
      setError(null);
      return;
    }

    if (!url || !needsAuth) {
      setObjectUrl("");
      setIsLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    let created = "";

    setIsLoading(true);
    setError(null);

    fetchAuthorizedObjectUrl(url, controller.signal)
      .then((next) => {
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        created = next;
        setObjectUrl(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "unknown error");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (created) URL.revokeObjectURL(created);
    };
  }, [url, needsAuth, canStream, isDecidingTransport]);

  return {
    src: !needsAuth || canStream ? (url ?? "") : objectUrl,
    isLoading,
    error,
  };
}
